import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useAuth } from "@likemagic-tech/sv-magic-library";
import { Loading } from "../components/loading";
import {
  SwaggerRequest,
  SwaggerUIWrapper,
} from "../components/swagger-ui-wrapper";

interface SwaggerUrl {
  url: string;
  name: string;
}

const useStyles = makeStyles((theme) => ({
  title: {
    flexGrow: 1,
    marginBottom: theme.spacing(2),
  },
  serviceSelector: {
    marginBottom: theme.spacing(2),
    minWidth: 300,
  },
  container: {
    width: "100%",
    "& .swagger-ui": {
      fontFamily: "inherit",
    },
    "& .swagger-ui .topbar": {
      display: "none",
    },
    // Hide the servers dropdown and authorize button - auth is handled automatically
    "& .swagger-ui .scheme-container": {
      display: "none",
    },
    // Hide the lock icons on individual endpoints
    "& .swagger-ui .authorization__btn": {
      display: "none",
    },
    // Hide the API docs URL link and external docs link (Vulnerability Disclosure Policy)
    "& .swagger-ui .info hgroup.main > a.link": {
      display: "none",
    },
    "& .swagger-ui .info > a.info__extdocs": {
      display: "none",
    },
  },
}));

// API URLs configuration (migrated from swagger-config.json)
const API_URLS: SwaggerUrl[] = [
  { url: "/api/cms/v3/api-docs", name: "cms-service" },
  {
    url: "/api/digital-access-service/v3/api-docs",
    name: "digital-access-service",
  },
  {
    url: "/api/food-and-beverage-connector/v3/api-docs",
    name: "food-and-beverage-connector",
  },
  { url: "/api/v3/api-docs", name: "gateway" },
  {
    url: "/api/event-and-data-hub-service/v3/api-docs",
    name: "event-and-data-hub-service",
  },
  {
    url: "/api/guest-identity-service/v3/api-docs",
    name: "guest-identity-service",
  },
  {
    url: "/api/guest-journey-service/v3/api-docs",
    name: "guest-journey-service",
  },
  { url: "/api/guest-service/v3/api-docs", name: "guest-service" },
  { url: "/api/hoko-service/v3/api-docs", name: "hoko-service" },
  { url: "/api/integration-service/v3/api-docs", name: "integration-service" },
  {
    url: "/api/job-scheduler-service/v3/api-docs",
    name: "job-scheduler-service",
  },
  { url: "/api/mews-connector/v3/api-docs", name: "mews-connector" },
  {
    url: "/api/notification-service/v3/api-docs",
    name: "notification-service",
  },
  { url: "/api/payment-service/v3/api-docs", name: "payment-service" },
  {
    url: "/api/pubsub-ingester-service/v3/api-docs",
    name: "pubsub-ingester-service",
  },
  { url: "/api/salto-event-stream/v3/api-docs", name: "salto-event-stream" },
  { url: "/api/sciant-connector/v3/api-docs", name: "sciant-connector" },
  {
    url: "/api/task-management-service/v3/api-docs",
    name: "task-management-service",
  },
  {
    url: "/api/tenant-management-service/v3/api-docs",
    name: "tenant-management-service",
  },
  { url: "/api/unified-messaging/v3/api-docs", name: "unified-messaging" },
  {
    url: "/api/task-management-v2-service/v3/api-docs",
    name: "task-management-service-v2",
  },
];

// Default to the tenant-management-service, looked up by name so that adding or removing an entry
// in API_URLS can't silently change the default.
const DEFAULT_SERVICE_URL = (
  API_URLS.find((service) => service.name === "tenant-management-service") ??
  API_URLS[0]
).url;

export const ApiDocsPage: React.FC = () => {
  const classes = useStyles();
  const auth = useAuth();
  const [selectedService, setSelectedService] =
    useState<string>(DEFAULT_SERVICE_URL);

  const handleServiceChange = (event: SelectChangeEvent<string>) => {
    setSelectedService(event.target.value);
  };

  // Periodically refresh the token to ensure Swagger UI requests use a valid token
  useEffect(() => {
    if (!auth.authenticated) return;

    const interval = setInterval(() => {
      // Refresh if token expires within 30 seconds
      auth.keycloak
        .updateToken(30)
        .then((refreshed) => {
          if (refreshed) {
            console.log("Token refreshed");
          }
        })
        .catch((error) => {
          console.error("Token refresh failed:", error);
        });
    }, 15 * 1000); // Check every 15 seconds

    return () => clearInterval(interval);
  }, [auth.authenticated, auth.keycloak]);

  // Store keycloak ref to access the latest token directly at request time
  const keycloakRef = useRef(auth.keycloak);
  keycloakRef.current = auth.keycloak;

  // Interceptor reads current token directly from keycloak at request time
  const requestInterceptor = useRef((req: SwaggerRequest): SwaggerRequest => {
    const token = keycloakRef.current?.token;
    if (token) {
      req.headers = {
        ...req.headers,
        Authorization: `Bearer ${token}`,
      };
    }
    return req;
  }).current;

  if (!auth.initialized) {
    return <Loading />;
  }

  if (!auth.authenticated) {
    return (
      <Box>
        <Typography
          component="h1"
          variant="h5"
          noWrap
          className={classes.title}
        >
          API Documentation
        </Typography>
        <Typography>Please log in to view the API documentation.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography component="h1" variant="h5" noWrap className={classes.title}>
        API Documentation
      </Typography>
      <FormControl className={classes.serviceSelector}>
        <InputLabel id="service-select-label">Service</InputLabel>
        <Select
          labelId="service-select-label"
          id="service-select"
          value={selectedService}
          label="Service"
          onChange={handleServiceChange}
        >
          {API_URLS.map((service) => (
            <MenuItem key={service.url} value={service.url}>
              {service.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box className={classes.container}>
        <SwaggerUIWrapper
          url={selectedService}
          requestInterceptor={requestInterceptor}
          token={auth.keycloak.token}
        />
      </Box>
    </Box>
  );
};
